import type {
  EvalCtx,
  HoldingTickInput,
  HoldingTickOutput,
  RoundEndOutput,
  RoundStartOutput,
  TradeCloseInput,
  TradeCloseOutput,
  Upgrade,
  UpgradeTrigger,
  RuntimeState,
  OpName,
} from "./types";
import {
  applyHoldingTick,
  applyRoundAccountOpsOnStart,
  applyRoundEndOps,
  reduceTradeClose,
} from "./registry";

interface InternalState {
  activeUpgrades: Upgrade[];
  nextRoundUpgrades: Upgrade[];
  charges: Record<string, number>;
  onceApplied: Record<string, boolean>;
  opTotals: Partial<Record<OpName, number>>;
  roundNumber: number;
  roundStartCash: number;
  firstWinApplied: boolean;
  firstLossApplied: boolean;
  currentTradeLeverageMult: number;
  currentTradeAutoLiqPct?: number;
}

export class UpgradesEngine {
  private s: InternalState;

  constructor() {
    this.s = {
      activeUpgrades: [],
      nextRoundUpgrades: [],
      charges: {},
      onceApplied: {},
      opTotals: {},
      roundNumber: 1,
      roundStartCash: 0,
      firstWinApplied: false,
      firstLossApplied: false,
      currentTradeLeverageMult: 1,
      currentTradeAutoLiqPct: undefined,
    };
  }

  public resetSession(): void {
    this.s.activeUpgrades = [];
    this.s.nextRoundUpgrades = [];
    this.s.charges = {};
    this.s.onceApplied = {};
    this.s.opTotals = {};
    this.s.roundNumber = 1;
    this.s.roundStartCash = 0;
    this.s.firstWinApplied = false;
    this.s.firstLossApplied = false;
    this.s.currentTradeLeverageMult = 1;
    this.s.currentTradeAutoLiqPct = undefined;
  }

  public clearRoundScopedUpgrades(): void {
    this.s.activeUpgrades = this.s.activeUpgrades.filter(
      (u) => u.scope !== "this_round"
    );
  }

  public offerNextRoundUpgrade(u: Upgrade): void {
    this.s.nextRoundUpgrades.push(u);
  }

  public addUpgrade(u: Upgrade): void {
    this.s.activeUpgrades.push(u);
  }

  public getActiveUpgrades(): ReadonlyArray<Upgrade> {
    return this.s.activeUpgrades;
  }

  private runtime(): RuntimeState {
    return {
      roundNumber: this.s.roundNumber,
      roundStartCash: this.s.roundStartCash,
      activeUpgrades: this.s.activeUpgrades,
      charges: this.s.charges,
      onceApplied: this.s.onceApplied,
      opTotals: this.s.opTotals,
    };
  }

  private filterUpgrades(
    trigger: UpgradeTrigger | string,
    predicate?: (u: Upgrade) => boolean
  ): Upgrade[] {
    return this.s.activeUpgrades.filter(
      (u) => u.trigger === trigger && (!predicate || predicate(u))
    );
  }

  private meetsLimits(u: Upgrade, scopeKey: string): boolean {
    const { limits } = u;
    if (!limits) return true;
    if (limits.charges != null) {
      const used = this.s.charges[u.id] ?? 0;
      if (used >= limits.charges) return false;
    }
    if (limits.once_per) {
      const k = `${u.id}::${limits.once_per}::${scopeKey}`;
      if (this.s.onceApplied[k]) return false;
    }
    return true;
  }

  private markApplied(u: Upgrade, scopeKey: string): void {
    if (u.limits?.charges != null) {
      this.s.charges[u.id] = (this.s.charges[u.id] ?? 0) + 1;
    }
    if (u.limits?.once_per) {
      const k = `${u.id}::${u.limits.once_per}::${scopeKey}`;
      this.s.onceApplied[k] = true;
    }
  }

  public onRoundStart(
    roundNumber: number,
    roundStartCash: number
  ): RoundStartOutput {
    this.s.roundNumber = roundNumber;
    this.s.roundStartCash = roundStartCash;
    this.s.firstWinApplied = false;
    this.s.firstLossApplied = false;
    // migrate nextRoundUpgrades → active if scope is next_round
    if (this.s.nextRoundUpgrades.length) {
      this.s.activeUpgrades.push(
        ...this.s.nextRoundUpgrades.filter((u) => u.scope === "next_round")
      );
      this.s.nextRoundUpgrades = [];
    }
    const trigger: UpgradeTrigger = "round_start";
    const list = this.filterUpgrades(trigger);
    const ctx: EvalCtx = {
      trigger,
      numbers: { roundStartCash },
      runtime: this.runtime(),
    };
    const usable = list.filter(
      (u) =>
        this.meetsLimits(u, `round:${roundNumber}`) &&
        (!u.condition || u.condition(ctx))
    );
    const effects = usable.flatMap((u) => u.effects);
    usable.forEach((u) => this.markApplied(u, `round:${roundNumber}`));
    return applyRoundAccountOpsOnStart(effects);
  }

  public onRoundEnd(): RoundEndOutput {
    const trigger: UpgradeTrigger = "round_end";
    const list = this.filterUpgrades(trigger);
    const ctx: EvalCtx = {
      trigger,
      numbers: { roundStartCash: this.s.roundStartCash },
      runtime: this.runtime(),
    };
    const usable = list.filter(
      (u) =>
        this.meetsLimits(u, `round:${this.s.roundNumber}`) &&
        (!u.condition || u.condition(ctx))
    );
    const effects = usable.flatMap((u) => u.effects);
    usable.forEach((u) => this.markApplied(u, `round:${this.s.roundNumber}`));
    return applyRoundEndOps(effects);
  }

  public onTradeOpen(): void {
    const trigger: UpgradeTrigger = "trade_open";
    const list = this.filterUpgrades(trigger);
    const ctx: EvalCtx = {
      trigger,
      numbers: { roundStartCash: this.s.roundStartCash },
      runtime: this.runtime(),
    };
    const usable = list.filter(
      (u) =>
        this.meetsLimits(u, `round:${this.s.roundNumber}`) &&
        (!u.condition || u.condition(ctx))
    );
    const effects = usable.flatMap((u) => u.effects);
    const acc = applyRoundAccountOpsOnStart(effects);
    this.s.currentTradeLeverageMult = acc.leverageMult || 1;
    this.s.currentTradeAutoLiqPct = acc.autoLiquidateDrawdownPct;
    usable.forEach((u) => this.markApplied(u, `round:${this.s.roundNumber}`));
  }

  public onHoldingTick(input: HoldingTickInput): HoldingTickOutput {
    const trigger: UpgradeTrigger = "holding_tick";
    const list = this.filterUpgrades(trigger);
    const ctx: EvalCtx = {
      trigger,
      numbers: { holdMs: input.holdMs },
      runtime: this.runtime(),
    };
    const usable = list.filter(
      (u) =>
        this.meetsLimits(u, `round:${this.s.roundNumber}`) &&
        (!u.condition || u.condition(ctx))
    );
    const effects = usable.flatMap((u) => u.effects);
    usable.forEach((u) => this.markApplied(u, `round:${this.s.roundNumber}`));
    return applyHoldingTick(input, effects);
  }

  public onTradeCloseWin(input: TradeCloseInput): TradeCloseOutput {
    const baseTrigger: UpgradeTrigger = "trade_close_win";
    const triggers: Array<UpgradeTrigger> = [baseTrigger];
    if (!this.s.firstWinApplied) triggers.push("first_win");
    if ((input.baseProfitPct ?? 0) >= 20) triggers.push("threshold_win_20");
    if ((input.baseProfitPct ?? 0) >= 30) triggers.push("threshold_win_30");

    let out: TradeCloseOutput = { fees: input.currentFees };
    for (const t of triggers) {
      const list = this.filterUpgrades(t);
      const ctx: EvalCtx = {
        trigger: t,
        numbers: {
          holdMs: input.holdMs,
          profitPct: input.baseProfitPct,
          roundStartCash: this.s.roundStartCash,
          currentFees: input.currentFees,
        },
        runtime: this.runtime(),
      };
      const usable = list.filter(
        (u) =>
          this.meetsLimits(u, `round:${this.s.roundNumber}`) &&
          (!u.condition || u.condition(ctx))
      );
      const effects = usable.flatMap((u) => u.effects);
      const r = reduceTradeClose(input, effects, ctx);
      out = { ...out, ...r };
      usable.forEach((u) => this.markApplied(u, `round:${this.s.roundNumber}`));
    }
    this.s.firstWinApplied = true;
    // reset trade-scoped params
    this.s.currentTradeLeverageMult = 1;
    this.s.currentTradeAutoLiqPct = undefined;
    return out;
  }

  public onTradeCloseLoss(input: TradeCloseInput): TradeCloseOutput {
    const baseTrigger: UpgradeTrigger = "trade_close_loss";
    const triggers: Array<UpgradeTrigger> = [baseTrigger];
    if (!this.s.firstLossApplied) triggers.push("first_loss");
    const lossAmt = ((input.baseLossPct ?? 0) / 100) * this.s.roundStartCash;
    if (lossAmt >= 0.1 * this.s.roundStartCash)
      triggers.push("threshold_loss_10");

    let out: TradeCloseOutput = { fees: input.currentFees };
    for (const t of triggers) {
      const list = this.filterUpgrades(t);
      const ctx: EvalCtx = {
        trigger: t,
        numbers: {
          holdMs: input.holdMs,
          lossPct: input.baseLossPct,
          lossAmount: lossAmt,
          roundStartCash: this.s.roundStartCash,
          currentFees: input.currentFees,
        },
        runtime: this.runtime(),
      };
      const usable = list.filter(
        (u) =>
          this.meetsLimits(u, `round:${this.s.roundNumber}`) &&
          (!u.condition || u.condition(ctx))
      );
      const effects = usable.flatMap((u) => u.effects);
      const r = reduceTradeClose(input, effects, ctx);
      out = { ...out, ...r };
      usable.forEach((u) => this.markApplied(u, `round:${this.s.roundNumber}`));
    }
    this.s.firstLossApplied = true;
    // reset trade-scoped params
    this.s.currentTradeLeverageMult = 1;
    this.s.currentTradeAutoLiqPct = undefined;
    return out;
  }

  public getCurrentTradeLeverageMult(): number {
    return this.s.currentTradeLeverageMult || 1;
  }

  public getCurrentTradeAutoLiqPct(): number | undefined {
    return this.s.currentTradeAutoLiqPct;
  }
}
